import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
  Alert,
  LinearProgress,
  FormControlLabel,
  Switch,
} from '@mui/material'
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material'
import { styled } from '@mui/material/styles'

export type DownloadType =
  | 'embeddings'
  | 'images'
  | 'cell-images-png'
  | 'cell-images-tiff'
  | 'morphotype-png'
  | 'morphotype-tiff'
  | 'csv'
  | 'tsv'
  | 'json'
  | 'run-report'

export interface DownloadItem {
  id: string
  type: DownloadType
  status: 'preparing' | 'ready' | 'downloading' | 'completed' | 'error'
  fileName: string
  downloadUrl?: string
  progress?: number
  elapsedTime?: number
  startTime?: number
  errorMessage?: string
  runId?: string
  runDetails?: string
  queuedForDownload?: boolean
}

interface DownloadPanelProps {
  downloads: DownloadItem[]
  onDownload: (item: DownloadItem, fileName: string) => void
  onCancel: (itemId: string) => void
  onDismiss: (itemId: string) => void
  onFileNameChange: (itemId: string, fileName: string) => void
  autoDownload?: boolean
  onAutoDownloadToggle?: (enabled: boolean) => void
  compact?: boolean
}

const StyledCard = styled(Card)(() => ({
  userSelect: 'none',
  '&:active': {
    cursor: 'grabbing',
  },
}))

const StatusChip = styled(Chip)<{ status: DownloadItem['status'] }>(({ theme, status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'preparing':
        return theme.palette.warning.main
      case 'ready':
        return theme.palette.success.main
      case 'downloading':
        return theme.palette.info.main
      case 'completed':
        return theme.palette.success.main
      case 'error':
        return theme.palette.error.main
      default:
        return theme.palette.grey[500]
    }
  }

  return {
    backgroundColor: getStatusColor(),
    color: 'white',
    fontWeight: 600,
    fontSize: '0.75rem',
  }
})

const getDownloadTypeLabel = (type: DownloadType): string => {
  const labels: Record<DownloadType, string> = {
    embeddings: 'Embeddings Export',
    images: 'Images Export',
    'cell-images-png': 'Cell Images (PNG)',
    'cell-images-tiff': 'Cell Images (TIFF)',
    'morphotype-png': 'Morphotype (PNG)',
    'morphotype-tiff': 'Morphotype (TIFF)',
    csv: 'CSV Export',
    tsv: 'TSV Export',
    json: 'JSON Export',
    'run-report': 'Run Report',
  }
  return labels[type] || 'Download'
}

const getFileExtension = (type: DownloadType): string => {
  const extensions: Record<DownloadType, string> = {
    embeddings: 'TSV',
    images: 'ZIP',
    'cell-images-png': 'ZIP',
    'cell-images-tiff': 'ZIP',
    'morphotype-png': 'ZIP',
    'morphotype-tiff': 'ZIP',
    csv: 'CSV',
    tsv: 'TSV',
    json: 'JSON',
    'run-report': 'CSV',
  }
  return extensions[type] || 'file'
}

const DownloadPanel: React.FC<DownloadPanelProps> = ({
  downloads,
  onDownload,
  onCancel,
  onDismiss,
  onFileNameChange,
  autoDownload = false,
  onAutoDownloadToggle,
  compact = false,
}) => {
  const [position, setPosition] = useState({ x: 20, y: 20 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [currentTime, setCurrentTime] = useState(Date.now())
  const cardRef = useRef<HTMLDivElement>(null)

  // Update current time every second to refresh elapsed time display
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now())
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  // Drag functionality
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target instanceof HTMLElement && e.target.closest('button, input, textarea')) {
      return // Don't drag if clicking on interactive elements
    }
    setIsDragging(true)
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    })
  }

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      })
    },
    [isDragging, dragStart]
  )

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
    return undefined
  }, [isDragging, dragStart, handleMouseMove])

  const getElapsedTime = (download: DownloadItem) => {
    if (download.status !== 'preparing') return null
    if (!download.startTime) return null
    const elapsed = Math.floor((currentTime - download.startTime) / 1000)
    return `${elapsed}s`
  }

  const getStatusIcon = (status: DownloadItem['status']) => {
    switch (status) {
      case 'preparing':
        return <ScheduleIcon fontSize="small" />
      case 'ready':
        return <DownloadIcon fontSize="small" />
      case 'downloading':
        return <CircularProgress size={16} />
      case 'completed':
        return <CheckCircleIcon fontSize="small" />
      case 'error':
        return <ErrorIcon fontSize="small" />
      default:
        return null
    }
  }

  const getStatusText = (download: DownloadItem) => {
    if (download.status === 'preparing') {
      const elapsed = getElapsedTime(download)
      return elapsed ? `Preparing... (${elapsed} elapsed)` : 'Preparing...'
    }

    switch (download.status) {
      case 'ready':
        return 'Ready to download'
      case 'downloading':
        return 'Downloading...'
      case 'completed':
        return 'Download completed'
      case 'error':
        return 'Download failed'
      default:
        return 'Unknown status'
    }
  }

  if (downloads.length === 0) return null

  return (
    <StyledCard
      ref={cardRef}
      onMouseDown={compact ? undefined : handleMouseDown}
      sx={{
        ...(compact
          ? {
              position: 'static',
              top: 'auto',
              right: 'auto',
              left: 'auto',
              zIndex: 'auto',
              minWidth: '100%',
              maxWidth: '100%',
              maxHeight: '400px',
              overflow: 'auto',
              boxShadow: 'none',
              borderRadius: 0,
              cursor: 'default',
            }
          : {
              position: 'fixed',
              left: position.x,
              top: position.y,
              right: 'auto',
              zIndex: 1300,
              minWidth: 400,
              maxWidth: 500,
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: (theme) => theme.shadows[8],
              borderRadius: 12,
              cursor: 'move',
            }),
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {!compact && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <DragIcon sx={{ color: 'text.secondary', cursor: 'grab' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Downloads
                </Typography>
              </Stack>
            )}
            <Stack direction="row" alignItems="center" spacing={2} sx={{ border: 'none' }}>
              {onAutoDownloadToggle && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoDownload}
                      onChange={(e) => onAutoDownloadToggle(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                      Auto-download
                    </Typography>
                  }
                  sx={{
                    margin: 0,
                    border: 'none',
                    '& .MuiFormControlLabel-label': {
                      border: 'none',
                    },
                  }}
                />
              )}
              <Typography variant="body2" color="text.secondary">
                {downloads.length} item{downloads.length !== 1 ? 's' : ''}
              </Typography>
            </Stack>
          </Stack>

          {downloads.map((download) => (
            <Box
              key={download.id}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}
            >
              <Stack spacing={2}>
                {/* Header with status */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    {getStatusIcon(download.status)}
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {getDownloadTypeLabel(download.type)}
                    </Typography>
                    <StatusChip
                      label={getStatusText(download)}
                      status={download.status}
                      size="small"
                    />
                  </Stack>
                  <IconButton
                    size="small"
                    onClick={() => onDismiss(download.id)}
                    disabled={download.status === 'downloading'}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>

                {/* Run details */}
                {download.runDetails && (
                  <Typography variant="body2" color="text.secondary">
                    {download.runDetails}
                  </Typography>
                )}

                {/* Progress bar for preparing */}
                {download.status === 'preparing' && (
                  <Box>
                    <LinearProgress />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: 'block' }}
                    >
                      File is being prepared. This may take a few minutes...
                    </Typography>
                  </Box>
                )}

                {/* Error message */}
                {download.status === 'error' && download.errorMessage && (
                  <Alert severity="error" sx={{ py: 1 }}>
                    {download.errorMessage}
                  </Alert>
                )}

                {/* File name input - always visible for editing */}
                <TextField
                  label="File name"
                  value={download.fileName}
                  onChange={(e) => onFileNameChange(download.id, e.target.value)}
                  size="small"
                  fullWidth
                  disabled={download.status === 'downloading'}
                  helperText={(() => {
                    const fileType = getFileExtension(download.type)
                    if (download.status === 'preparing') {
                      return download.queuedForDownload
                        ? `File is being prepared... will download automatically when ready (${fileType} format)`
                        : `Edit filename while file is being prepared... (will be saved as ${fileType})`
                    }
                    if (download.status === 'ready') {
                      return `Filename ready - click Download Now (${fileType} format)`
                    }
                    return `You can edit the filename before downloading (will be saved as ${fileType})`
                  })()}
                />

                {/* Action buttons */}
                <Stack direction="row" spacing={1}>
                  {(download.status === 'ready' || download.status === 'preparing') && (
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={() => onDownload(download, download.fileName)}
                      fullWidth
                      disabled={download.status === 'preparing' && !download.queuedForDownload}
                    >
                      {(() => {
                        if (download.status === 'preparing') {
                          return download.queuedForDownload ? 'Queued for Download' : 'Preparing...'
                        }
                        return 'Download Now'
                      })()}
                    </Button>
                  )}

                  {download.status === 'preparing' && (
                    <Button variant="outlined" onClick={() => onCancel(download.id)} fullWidth>
                      Cancel
                    </Button>
                  )}

                  {download.status === 'downloading' && (
                    <Button variant="outlined" disabled fullWidth>
                      Downloading...
                    </Button>
                  )}

                  {download.status === 'completed' && (
                    <Button variant="outlined" onClick={() => onDismiss(download.id)} fullWidth>
                      Dismiss
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </StyledCard>
  )
}

export default DownloadPanel
